
import React, { useState, useRef } from 'react';
import {Box, Typography, TextField, CircularProgress, Button} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FileUpload } from '@/components/ui/FileUpload';
import { store } from '@/service/store';
import { Alert } from '@mui/material';

interface VehicleDetailsStepProps {
  setIsValid?: (isValid: boolean) => void;
  onNextStep?: () => void;
}

const vehicleFormSchema = z.object({
  hsn: z.string()
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 1 && num <= 9999;
    }, { message: "HSN must be between 1 and 9999" }),
  tsn: z.string()
    .length(3, "TSN must be exactly 3 characters"),
  year: z.string()
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 1970 && num <= new Date().getFullYear();
    }, { message: `Year must be between 1970 and ${new Date().getFullYear()}` }),
  firstRegistrationMonth: z.string()
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 1 && num <= 12;
    }, { message: "Month must be between 1 and 12" }),
  firstRegistrationYear: z.string()
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 1970 && num <= new Date().getFullYear();
    }, { message: `Year must be between 1970 and ${new Date().getFullYear()}` }),
  vehicleRegistration: z.string({ required_error: "Document upload is required" })
    .min(1, "Document upload is required")
});

type VehicleFormValues = z.infer<typeof vehicleFormSchema>;

const VehicleDetailsStep: React.FC<VehicleDetailsStepProps> = ({ setIsValid, onNextStep }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    watch
  } = useForm<VehicleFormValues>({
    resolver: zodResolver(vehicleFormSchema),
    defaultValues: {
      hsn: '',
      tsn: '',
      year: '',
      firstRegistrationMonth: '',
      firstRegistrationYear: '',
      vehicleRegistration: ''
    },
    mode: 'onChange'
  });

  React.useEffect(() => {
    setIsValid?.(isValid);
  }, [isValid, setIsValid]);

  React.useEffect(() => {
    // Define the function on the window object
    window.submitVehicleDetailsForm = () => {
      if (formRef.current) {
        formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        return true;
      }
      return false;
    };

    // Clean up when component unmounts
    return () => {
      delete window.submitVehicleDetailsForm;
    };
  }, []);

  const documentUuid = watch('vehicleRegistration');

  const onSubmit = async (data: VehicleFormValues) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const customerNumber = store.installationAssistant.customerNumber.value;
      const customer = store.customers.get(customerNumber);
      const vehicleVIN = store.installationAssistant.vehicleVIN.value;

      const payload = {
        vehicleRegistration: data.vehicleRegistration,
        vin: vehicleVIN,
        year: parseInt(data.year),
        firstRegistrationMonth: parseInt(data.firstRegistrationMonth),
        firstRegistrationYear: parseInt(data.firstRegistrationYear),
        hsn: parseInt(data.hsn),
        tsn: data.tsn
      };

      await customer.cars.add(payload);

      onNextStep?.();
    } catch (err) {
      console.error('Error submitting vehicle details:', err);
      setError(t('assistant.vehicleDetailsStep.submissionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSkip = async () => {
    const customerNumber = store.installationAssistant.customerNumber.value;
    const customer = store.customers.get(customerNumber);
    const vehicleVIN = store.installationAssistant.vehicleVIN.value;

    const payload = {
      vin: vehicleVIN,
    };

    await customer.cars.add(payload);

    onNextStep?.();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} sx={{mb: 3}}>
        <Grid size={{xs:12 , sm: "grow"}}>
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
            {t('assistant.vehicleDetailsStep.title')}
          </Typography>
        </Grid>
        <Grid>
          <Button variant="outlined" color="primary" sx={{ mr: 2 }} size="small" onClick={onSkip}>
            {t('assistant.skip')}
          </Button>
        </Grid>
      </Grid>

      <Typography variant="body2" sx={{ mb: 3 }}>
        {t('assistant.vehicleDetailsStep.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="HSN"
              type="number"
              {...register('hsn')}
              error={!!errors.hsn}
              helperText={errors.hsn?.message}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="TSN"
              slotProps={{ input: { inputProps: { maxLength: 3 } } }}
              {...register('tsn')}
              error={!!errors.tsn}
              helperText={errors.tsn?.message}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('assistant.vehicleDetailsStep.productionYear')}
              type="number"
              {...register('year')}
              error={!!errors.year}
              helperText={errors.year?.message}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('assistant.vehicleDetailsStep.registrationMonth')}
              type="number"
              {...register('firstRegistrationMonth')}
              error={!!errors.firstRegistrationMonth}
              helperText={errors.firstRegistrationMonth?.message}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('assistant.vehicleDetailsStep.registrationYear')}
              type="number"
              {...register('firstRegistrationYear')}
              error={!!errors.firstRegistrationYear}
              helperText={errors.firstRegistrationYear?.message}
            />
          </Grid>

          <Grid size={12}>
            <FileUpload
              label={t('assistant.vehicleDetailsStep.uploadDocument')}
              value={documentUuid}
              onChange={(value) => setValue('vehicleRegistration', value || '', { shouldValidate: true })}
              accept="image/*,application/pdf"
              id="vehicle-document-upload"
            />
            {errors.vehicleRegistration && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                {errors.vehicleRegistration.message}
              </Typography>
            )}
          </Grid>
        </Grid>
      </form>

      {isSubmitting && (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default VehicleDetailsStep;
